import {
  Box,
  Button,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import {
  ICrmBrandLocationData,
  useGetCrmBrandLocationsApi,
} from 'apis/crm-brand-locations/use-get-crm-brand-locations-api';
import { useUpdateCrmBrandLocationApi } from 'apis/crm-brand-locations/use-update-crm-brand-location-api';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FiEdit3, FiMapPin } from 'react-icons/fi';

interface EditBrandLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
}

interface FormData {
  name: string;
  address?: string;
  landlineNumber?: string;
  type: 'physical_location' | 'cloud_kitchen';
  area?: string;
}

const EditBrandLocationModal: React.FC<EditBrandLocationModalProps> = ({ isOpen, onClose, locationId }) => {
  const toast = useCustomToast();
  const { data: locationsData } = useGetCrmBrandLocationsApi();

  // Find the current location data
  const currentLocation = locationsData?.data?.find((location: ICrmBrandLocationData) => location.id === locationId);

  const { mutate: updateLocation, isLoading: isUpdating } = useUpdateCrmBrandLocationApi({
    onSuccess: () => {
      toast({
        title: 'Success!',
        description: 'Brand location updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update location. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      address: '',
      landlineNumber: '',
      type: 'physical_location',
      area: '',
    },
  });

  // Update form values when location data is available
  useEffect(() => {
    if (currentLocation) {
      reset({
        name: currentLocation.name || '',
        address: currentLocation.address || '',
        landlineNumber: currentLocation.landlineNumber || '',
        type: currentLocation.type || 'physical_location',
        area: currentLocation.area || '',
      });
    }
  }, [currentLocation, reset]);

  const onSubmit = useCallback(
    (data: FormData) => {
      updateLocation({
        id: locationId,
        data: {
          name: data.name.trim(),
          address: data.address?.trim() || undefined,
          landlineNumber: data.landlineNumber?.trim() || undefined,
          type: data.type,
          area: data.area?.trim() || undefined,
        },
      });
    },
    [updateLocation, locationId]
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  if (!currentLocation) {
    return (
      <Modal isOpen={isOpen} onClose={onCloseModal} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Center py={8}>
              <VStack spacing={4}>
                <Spinner size='lg' color='blue.500' />
                <Text>Loading location details...</Text>
              </VStack>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <FiEdit3 color='#3182CE' />
            <Text>Edit Brand Location</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align='stretch'>
            {/* Brand Info (Read-only) */}
            <Box>
              <Text fontWeight='semibold' color='gray.700' mb={4}>
                🏢 Brand Information
              </Text>
              <HStack spacing={3} p={3} bg='gray.50' borderRadius='md'>
                {currentLocation.brand.image && (
                  <div style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden' }}>
                    <img
                      src={currentLocation.brand.image}
                      alt={currentLocation.brand.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <Text fontWeight='medium'>{currentLocation.brand.name}</Text>
              </HStack>
            </Box>

            <Divider />

            {/* Location Details */}
            <Box>
              <HStack spacing={2} mb={4}>
                <FiMapPin color='#3182CE' />
                <Text fontWeight='semibold' color='gray.700'>
                  Location Details
                </Text>
              </HStack>

              <VStack spacing={4} align='stretch'>
                <FormControl isInvalid={!!errors.name} isRequired>
                  <FormLabel>Location Name</FormLabel>
                  <Input
                    placeholder='e.g., Downtown Branch'
                    {...register('name', {
                      required: 'Location name is required',
                      maxLength: {
                        value: 255,
                        message: 'Name must be less than 255 characters',
                      },
                      validate: (value) => {
                        const trimmed = value.trim();
                        return trimmed.length > 0 || 'Location name cannot be empty';
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.type}>
                  <FormLabel>Location Type</FormLabel>
                  <Select {...register('type')}>
                    <option value='physical_location'>Physical Location</option>
                    <option value='cloud_kitchen'>Cloud Kitchen</option>
                  </Select>
                  <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.address}>
                  <FormLabel>Address</FormLabel>
                  <Textarea placeholder='Full address of the location' rows={3} {...register('address')} />
                  <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.area}>
                  <FormLabel>Area/District</FormLabel>
                  <Input
                    placeholder='e.g., Downtown Dubai'
                    {...register('area', {
                      maxLength: {
                        value: 255,
                        message: 'Area must be less than 255 characters',
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.area?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.landlineNumber}>
                  <FormLabel>Landline Number</FormLabel>
                  <Input
                    placeholder='e.g., +97143456789'
                    {...register('landlineNumber', {
                      maxLength: {
                        value: 20,
                        message: 'Phone number must be less than 20 characters',
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.landlineNumber?.message}</FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onCloseModal} isDisabled={isUpdating}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSubmit(onSubmit)}
              isLoading={isUpdating}
              loadingText='Updating...'
              isDisabled={!isValid}>
              Update Location
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditBrandLocationModal;
